import { useState } from "react";
import axios from "axios";
import { API } from "@/App";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import DateRangePicker, { defaultRange } from "@/components/DateRangePicker";
import { toast } from "sonner";
import {
  FileDown, Loader2, Package, RotateCcw, PackageX, Clock, FileText, Banknote,
  Scale, Wallet, TrendingUp, Globe, Truck, AlertTriangle, Ban, AlertOctagon,
} from "lucide-react";

// The 14 B2C reports (data-driven). `date` = show an optional From/To range;
// `stuck` = show the "stuck after N days" input. Endpoints: /api/b2c/reports/<key>.
const REPORTS = [
  { key: "orders", title: "Order-wise shipment status", desc: "Every B2C order with its current status, courier, zone, weights and key dates.", icon: Package, date: true },
  { key: "rto-returns", title: "RTO / Return report", desc: "Forward orders that went RTO, plus seller-raised returns & exchanges.", icon: RotateCcw, date: true },
  { key: "delivery-attempts", title: "Delivery attempt report", desc: "Failed-delivery (NDR) orders with reason, attempt count and action history.", icon: PackageX, date: true },
  { key: "ageing", title: "Shipment ageing report", desc: "In-flight shipments by age and days in the current status — oldest first.", icon: Clock, date: false },
  { key: "billing-summary", title: "Invoice / billing summary", desc: "Per-order charge breakdown (freight, COD, GST, total) for shipped orders.", icon: FileText, date: true },
  { key: "cod-remittance", title: "COD remittance report", desc: "Each COD order's collection + payout state, with the payout reference.", icon: Banknote, date: true },
  { key: "weight-discrepancy", title: "Weight discrepancy report", desc: "Declared vs courier-applied weight, the extra charge and dispute status.", icon: Scale, date: true },
  { key: "wallet-ledger", title: "Wallet / ledger statement", desc: "Every B2C wallet debit/credit with running balance and description.", icon: Wallet, date: true },
  { key: "delivery-performance", title: "Delivery performance report", desc: "Turnaround time (TAT) from AWB assignment to delivery, by courier & zone.", icon: TrendingUp, date: true },
  { key: "zone", title: "Zone / pincode report", desc: "Order mix and outcomes grouped by delivery zone.", icon: Globe, date: true },
  { key: "courier-partner", title: "Courier partner report", desc: "Order mix, delivery % and RTO % grouped by courier.", icon: Truck, date: true },
  { key: "rto-analysis", title: "RTO rate analysis", desc: "RTO % of shipped orders by courier — highest first.", icon: AlertTriangle, date: true },
  { key: "lost-damaged", title: "Lost / damaged shipments", desc: "Orders marked lost, plus returns raised as damaged / defective.", icon: Ban, date: true },
  { key: "stuck-exceptions", title: "Stuck / exception shipments", desc: "In-flight orders with no movement for N+ days, or sitting in NDR.", icon: AlertOctagon, date: false, stuck: true },
];

const B2CReportsSection = () => {
  // Each date-report defaults to the current month (a bounded range — keeps
  // exports light on the server vs an accidental full-table scan).
  const [dates, setDates] = useState(() => {
    const init = {};
    REPORTS.forEach((r) => { if (r.date) init[r.key] = defaultRange("this_month"); });
    return init;
  });
  const [stuckDays, setStuckDays] = useState(3);
  const [downloading, setDownloading] = useState({});

  const download = async (r) => {
    setDownloading((p) => ({ ...p, [r.key]: true }));
    try {
      const params = new URLSearchParams();
      if (r.date) {
        const d = dates[r.key] || {};
        if (d.start) params.append("start_date", d.start);
        if (d.end) params.append("end_date", d.end);
      }
      if (r.stuck) params.append("stuck_days", String(stuckDays || 3));
      const res = await axios.get(`${API}/b2c/reports/${r.key}?${params.toString()}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `${r.key.replace(/-/g, "_")}_${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Report downloaded");
    } catch (e) {
      toast.error(e?.response?.status === 422 ? "B2C is not enabled for this account" : "Failed to download report");
    } finally {
      setDownloading((p) => ({ ...p, [r.key]: false }));
    }
  };

  return (
    <div className="grid gap-5 sm:grid-cols-2 xl:grid-cols-3">
      {REPORTS.map((r) => {
        const Icon = r.icon;
        const d = dates[r.key] || {};
        const busy = !!downloading[r.key];
        return (
          <Card key={r.key} className="border-slate-200 flex flex-col">
            <CardHeader className="pb-3">
              <div className="flex items-center gap-2">
                <Icon className="w-5 h-5 text-brand shrink-0" />
                <CardTitle className="text-base leading-tight">{r.title}</CardTitle>
              </div>
              <CardDescription className="text-xs">{r.desc}</CardDescription>
            </CardHeader>
            <CardContent className="mt-auto space-y-3">
              {r.date && (
                <DateRangePicker value={d} onChange={(v) => setDates((p) => ({ ...p, [r.key]: v }))} />
              )}
              {r.stuck && (
                <div className="space-y-1">
                  <Label className="text-xs text-slate-500">Stuck after (days)</Label>
                  <Input type="number" min="1" max="60" value={stuckDays} onChange={(e) => setStuckDays(e.target.value)} />
                </div>
              )}
              <Button onClick={() => download(r)} disabled={busy} className="w-full" data-testid={`b2c-report-${r.key}`}>
                {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileDown className="w-4 h-4 mr-2" />}
                Download CSV
              </Button>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
};

export default B2CReportsSection;
